import axios from 'axios';
import { apiClient } from './apiClient';
import { authStore, type AuthUser } from '@/auth/authStore';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5001';

interface AuthResponse {
  accessToken: string;
  accessTokenExpiresUtc: string;
  refreshToken: string;
  refreshTokenExpiresUtc: string;
  user: AuthUser;
  /** The password was right but a second factor is needed: finish with verifyMfa. */
  mfaRequired?: boolean;
  /** The organisation requires an authenticator and this account has none: set one up with enrolMfa, then verifyMfa. */
  enrolmentRequired?: boolean;
  mfaToken?: string | null;
  /** Recovery codes, shown once, when the authenticator was set up during this sign-in. */
  recoveryCodes?: string[] | null;
  /** With a challenge: which second factors the account can answer with. */
  mfaMethods?: MfaMethod[] | null;
  /** With an enrolment: the organisation lets the person set a sign-in PIN instead of an authenticator app. */
  pinAllowed?: boolean;
}

/** A sign-in that stopped at the second factor. */
/** The second factors an account can answer with: the code from an authenticator app, and/or a sign-in PIN. */
export type MfaMethod = 'Authenticator' | 'Pin';
export interface MfaChallenge {
  mfaRequired: true;
  enrolmentRequired: boolean;
  mfaToken: string;
  methods: MfaMethod[];
  /** When enrolling: the organisation lets the person set a sign-in PIN instead of an authenticator app. */
  pinAllowed: boolean;
}

export interface MfaStatus {
  enabled: boolean;
  recoveryCodesLeft: number;
  requiredByOrganisation: boolean;
  authenticatorEnabled: boolean;
  pinSet: boolean;
  /** The organisation accepts a sign-in PIN as the second factor. */
  pinAllowed: boolean;
  /** "self", or "administrator" when it was predefined. */
  pinSetBy: string | null;
  pinSetDate: string | null;
}
export interface MfaSetup { sharedKey: string; authenticatorUri: string; /** During sign-in: the fresh challenge token to finish with. */ mfaToken?: string | null }
export interface EntraConfig { enabled: boolean; authority: string; tenantId: string; clientId: string }
export interface SessionPolicy { accessTokenMinutes: number; refreshTokenDays: number; idleTimeoutMinutes: number; lockoutAttempts: number; lockoutMinutes: number; absoluteSessionHours?: number }

/** The organisation's session policy the signed-in client follows (its idle timeout). */
export async function getSessionPolicy(): Promise<SessionPolicy> { return (await apiClient.get<SessionPolicy>('/api/v1/auth/session-policy')).data; }

function friendly(err: unknown, fallback: string): Error {
  if (axios.isAxiosError(err)) {
    const message = (err.response?.data as { error?: string })?.error;
    return new Error(message ?? fallback);
  }
  return err as Error;
}

/** Authenticates and stores the session, or returns the second-factor challenge. Throws a friendly message on failure. */
export async function login(email: string, password: string): Promise<AuthUser | MfaChallenge> {
  try {
    // Use a bare axios call so a 401 here is not intercepted as a token-refresh attempt.
    const { data } = await axios.post<AuthResponse>(
      `${baseURL}/api/v1/auth/login`,
      { email, password },
      { headers: { 'Content-Type': 'application/json' } },
    );
    if (data.mfaRequired && data.mfaToken) {
      return { mfaRequired: true, enrolmentRequired: Boolean(data.enrolmentRequired), mfaToken: data.mfaToken, methods: data.mfaMethods ?? [], pinAllowed: Boolean(data.pinAllowed) };
    }
    authStore.getState().setSession(data.user, data.accessToken, data.refreshToken);
    return data.user;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const message = (err.response?.data as { error?: string })?.error;
      throw new Error(message ?? 'Unable to sign in. Please try again.');
    }
    throw err;
  }
}

/** Finishes a sign-in with the authenticator (or recovery) code; recovery codes come back when the authenticator was just set up. */
export async function verifyMfa(mfaToken: string, code: string): Promise<{ user: AuthUser; recoveryCodes: string[] | null }> {
  try {
    const { data } = await axios.post<AuthResponse>(`${baseURL}/api/v1/auth/mfa/verify`, { mfaToken, code }, { headers: { 'Content-Type': 'application/json' } });
    authStore.getState().setSession(data.user, data.accessToken, data.refreshToken);
    return { user: data.user, recoveryCodes: data.recoveryCodes ?? null };
  } catch (err) { throw friendly(err, 'Unable to verify the code.'); }
}

/** When the organisation requires an authenticator and the account has none: the key to set one up during sign-in. */
export async function enrolMfa(mfaToken: string): Promise<MfaSetup> {
  try {
    return (await axios.post<MfaSetup>(`${baseURL}/api/v1/auth/mfa/enrol`, { mfaToken }, { headers: { 'Content-Type': 'application/json' } })).data;
  } catch (err) { throw friendly(err, 'Unable to start the authenticator set-up.'); }
}

export async function getMfaStatus(): Promise<MfaStatus> { return (await apiClient.get<MfaStatus>('/api/v1/auth/mfa')).data; }
export async function setupMfa(): Promise<MfaSetup> {
  try { return (await apiClient.post<MfaSetup>('/api/v1/auth/mfa/setup')).data; } catch (err) { throw friendly(err, 'Unable to start the set-up.'); }
}
export async function enableMfa(code: string): Promise<{ recoveryCodes: string[] }> {
  try { return (await apiClient.post<{ recoveryCodes: string[] }>('/api/v1/auth/mfa/enable', { code })).data; } catch (err) { throw friendly(err, 'Unable to turn on the authenticator.'); }
}
/** Finishes an enrolment with a sign-in PIN instead of an authenticator app, and signs in. */
export async function enrolPin(mfaToken: string, pin: string): Promise<AuthUser> {
  try {
    const { data } = await axios.post<AuthResponse>(`${baseURL}/api/v1/auth/mfa/enrol-pin`, { mfaToken, pin }, { headers: { 'Content-Type': 'application/json' } });
    authStore.getState().setSession(data.user, data.accessToken, data.refreshToken);
    return data.user;
  } catch (err) { throw friendly(err, 'Unable to set the PIN.'); }
}
/** Sets or changes the signed-in person's PIN; the password proves it is them. */
export async function setMfaPin(pin: string, password: string): Promise<MfaStatus> {
  try { return (await apiClient.post<MfaStatus>('/api/v1/auth/mfa/pin', { pin, password })).data; } catch (err) { throw friendly(err, 'Unable to set the PIN.'); }
}
export async function removeMfaPin(password: string): Promise<MfaStatus> {
  try { return (await apiClient.post<MfaStatus>('/api/v1/auth/mfa/pin/remove', { password })).data; } catch (err) { throw friendly(err, 'Unable to remove the PIN.'); }
}
export async function disableMfa(code: string): Promise<void> {
  try { await apiClient.post('/api/v1/auth/mfa/disable', { code }); } catch (err) { throw friendly(err, 'Unable to remove the authenticator.'); }
}

/** Whether Microsoft sign-in is on, and what the browser needs to start it. */
export async function getEntraConfig(): Promise<EntraConfig> {
  return (await axios.get<EntraConfig>(`${baseURL}/api/v1/auth/entra`)).data;
}

/** Sends the browser to Microsoft; it comes back to the sign-in page with an ID token in the URL fragment. */
export function startMicrosoftSignIn(config: EntraConfig, from: string) {
  const nonce = crypto.randomUUID();
  sessionStorage.setItem('entra.nonce', nonce);
  sessionStorage.setItem('entra.from', from);
  const redirect = `${window.location.origin}/login`;
  const params = new URLSearchParams({
    client_id: config.clientId, response_type: 'id_token', redirect_uri: redirect, response_mode: 'fragment',
    scope: 'openid profile email', nonce, prompt: 'select_account',
  });
  window.location.assign(`${config.authority}/oauth2/v2.0/authorize?${params.toString()}`);
}

/** Exchanges the ID token Microsoft returned for this API's session. */
export async function loginWithMicrosoft(idToken: string): Promise<AuthUser> {
  try {
    const { data } = await axios.post<AuthResponse>(`${baseURL}/api/v1/auth/entra`, { idToken }, { headers: { 'Content-Type': 'application/json' } });
    authStore.getState().setSession(data.user, data.accessToken, data.refreshToken);
    return data.user;
  } catch (err) { throw friendly(err, 'The Microsoft sign-in could not be completed.'); }
}

export interface SamlConfig { enabled: boolean; displayName: string }

/** Whether SAML sign-in is on (docs §169), and what its button says. */
export async function getSamlConfig(): Promise<SamlConfig> {
  return (await axios.get<SamlConfig>(`${baseURL}/api/v1/auth/saml`)).data;
}

/** Sends the browser to the organisation's identity provider; it comes back to the sign-in page with a one-time code. */
export function startSamlSignIn(from: string) {
  sessionStorage.setItem('saml.from', from);
  window.location.assign(`${baseURL}/api/v1/auth/saml/login`);
}

/** Exchanges the one-time code for this API's session. */
export async function loginWithSaml(code: string): Promise<AuthUser> {
  try {
    const { data } = await axios.post<AuthResponse>(`${baseURL}/api/v1/auth/saml/exchange`, { code }, { headers: { 'Content-Type': 'application/json' } });
    authStore.getState().setSession(data.user, data.accessToken, data.refreshToken);
    return data.user;
  } catch (err) { throw friendly(err, 'The sign-in could not be completed.'); }
}

/** Revokes the refresh token server-side and clears the local session. */
export async function logout(): Promise<void> {
  const { refreshToken } = authStore.getState();
  try {
    if (refreshToken) {
      await apiClient.post('/api/v1/auth/revoke', { refreshToken });
    }
  } catch {
    // Best-effort; clear the session regardless.
  } finally {
    authStore.getState().clear();
  }
}

interface MessageResponse {
  message: string;
  /** In Development the API returns the reset token so the flow is testable without a mailbox. */
  token?: string | null;
}

/** Requests a password-reset token. Returns the dev token when the API provides one. */
export async function forgotPassword(email: string): Promise<MessageResponse> {
  const { data } = await axios.post<MessageResponse>(
    `${baseURL}/api/v1/auth/forgot-password`,
    { email },
    { headers: { 'Content-Type': 'application/json' } },
  );
  return data;
}

/** Completes a password reset using the emailed token. */
export async function resetPassword(email: string, token: string, newPassword: string): Promise<void> {
  try {
    await axios.post(
      `${baseURL}/api/v1/auth/reset-password`,
      { email, token, newPassword },
      { headers: { 'Content-Type': 'application/json' } },
    );
  } catch (err) {
    if (axios.isAxiosError(err)) {
      throw new Error((err.response?.data as { error?: string })?.error ?? 'Unable to reset password.');
    }
    throw err;
  }
}

/** Changes the current user's password (clears any must-change flag server-side). */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  try {
    await apiClient.post('/api/v1/auth/change-password', { currentPassword, newPassword });
  } catch (err) {
    if (axios.isAxiosError(err)) {
      throw new Error((err.response?.data as { error?: string })?.error ?? 'Unable to change password.');
    }
    throw err;
  }
}

/** Refreshes the current user's profile/permissions from the API. */
export async function fetchMe(): Promise<AuthUser> {
  const { data } = await apiClient.get<AuthUser>('/api/v1/auth/me');
  const { accessToken, refreshToken } = authStore.getState();
  if (accessToken && refreshToken) {
    authStore.getState().setSession(data, accessToken, refreshToken);
  }
  return data;
}

// ----------------------------- Organisations (tenants) -----------------------------

export interface SignupOrganisationInput {
  organisationName: string;
  fullName: string;
  email: string;
  password: string;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  isHost: boolean;
  isActive: boolean;
  allowSelfRegistration: boolean;
  createdDate: string;
}

/** Creates a new organisation with its first administrator and signs them in. */
export async function signupOrganisation(input: SignupOrganisationInput): Promise<AuthUser> {
  try {
    const { data } = await axios.post<AuthResponse>(`${baseURL}/api/v1/tenants/signup`, input, {
      headers: { 'Content-Type': 'application/json' },
    });
    authStore.getState().setSession(data.user, data.accessToken, data.refreshToken);
    return data.user;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const message = (err.response?.data as { error?: string })?.error;
      throw new Error(message ?? 'Unable to create the organisation. Please try again.');
    }
    throw err;
  }
}

/** The signed-in user's organisation. */
export async function fetchMyTenant(): Promise<TenantInfo> {
  const { data } = await apiClient.get<TenantInfo>('/api/v1/tenants/me');
  return data;
}
